// "Submitted by" filter for the agent queues (Agents → Queues). A job's
// submitter is ai_jobs.created_by; scheduled jobs (keyword sweeps) have none.

import { supabase } from './supabase';
import type { AiJob } from './aiJobs';

/** 'all', 'scheduled' (no submitter), or a user id. */
export type SubmitterFilter = string;

export const SUBMITTER_ALL = 'all';
export const SUBMITTER_SCHEDULED = 'scheduled';

const STORAGE_KEY = 'agent-queue-submitter';

export interface Submitter {
  id: string;
  name: string;
}

/** Everyone who has submitted a job recently, first names, most recent first. */
export async function fetchSubmitters(): Promise<Submitter[]> {
  const { data: jobs, error } = await supabase
    .from('ai_jobs')
    .select('created_by')
    .not('created_by', 'is', null)
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  const ids = [...new Set((jobs ?? []).map((j) => j.created_by as string))];
  if (ids.length === 0) return [];

  const { data: users, error: usersError } = await supabase.from('users').select('id, name').in('id', ids);
  if (usersError) throw usersError;
  const names = new Map((users ?? []).map((u) => [u.id, (u.name || '').trim()]));
  const firsts = ids.map((id) => (names.get(id) || 'Unknown').split(/\s+/)[0]);
  // Full names only where two people share a first name.
  return ids.map((id, i) => ({
    id,
    name: firsts.filter((f) => f === firsts[i]).length > 1 ? names.get(id) || firsts[i] : firsts[i],
  }));
}

export function matchesSubmitter(job: AiJob, filter: SubmitterFilter): boolean {
  if (filter === SUBMITTER_ALL) return true;
  if (filter === SUBMITTER_SCHEDULED) return !job.created_by;
  return job.created_by === filter;
}

/**
 * Drag-reorder inside a filtered list: the visible jobs take back the slots
 * they held in the full queue, in their new order, so hidden jobs keep their
 * places. reorderQueueJobs writes positions for the whole list.
 */
export function mergeVisibleOrder(all: AiJob[], visibleNext: AiJob[]): AiJob[] {
  const visible = new Set(visibleNext.map((job) => job.id));
  let i = 0;
  return all.map((job) => (visible.has(job.id) ? visibleNext[i++] : job));
}

export function loadSubmitterFilter(): SubmitterFilter {
  try {
    return localStorage.getItem(STORAGE_KEY) || SUBMITTER_ALL;
  } catch {
    return SUBMITTER_ALL;
  }
}

export function saveSubmitterFilter(filter: SubmitterFilter): void {
  try {
    localStorage.setItem(STORAGE_KEY, filter);
  } catch {
    // storage unavailable: the filter just resets next visit
  }
}
